import { OrbitOutcome } from '../../../types';
import {
  OrbitAction,
  OrbitActionBuilder,
  OrbitActionButton,
  OrbitActionCandidate,
  OrbitActionFieldValue,
  OrbitActionMode,
  OrbitActionStep,
  OrbitActionTarget
} from '../../types';

/** What one demo turn ends as: the fields a completed turn carries on the wire. */
export interface IDemoReply {
  outcome: OrbitOutcome;
  answer: string;
  /** Left out for a turn that took no part in an action, as the server does. */
  action?: OrbitAction;
}

export interface IDemoFlow {
  id: string;
  /** The suggested prompt, sent as an ordinary question. */
  prompt: string;
  reply: (now: number) => IDemoReply;
  /** The turn `select` answers with for the picked candidate. */
  select?: (candidate: OrbitActionCandidate, now: number) => IDemoReply;
  /** What the confirming turn ends as; `expired` answers the confirm itself with 410 / 41001. */
  confirm?: (proposal: OrbitAction) => IDemoReply | 'expired';
}

/** The contract's 15 minute proposal window. */
export const PROPOSAL_TTL_MS = 15 * 60 * 1000;

export const step = (
  type: string,
  label: string,
  target: OrbitActionTarget,
  fields?: Record<string, OrbitActionFieldValue>
): OrbitActionStep => ({
  id: 's1',
  type,
  label,
  target,
  status: 'proposed',
  result: null,
  link: null,
  ...(fields === undefined ? {} : { fields })
});

export const proposal = (
  proposed: OrbitActionStep,
  now: number,
  extras: {
    buttons?: OrbitActionButton[];
    builder?: OrbitActionBuilder;
    ttlMs?: number;
    mode?: OrbitActionMode;
  } = {}
): OrbitAction => ({
  state: 'awaiting_confirmation',
  expires_at: new Date(now + (extras.ttlMs ?? PROPOSAL_TTL_MS)).toISOString(),
  steps: [proposed],
  candidates: [],
  ...(extras.buttons === undefined
    ? {}
    : { mode: extras.mode ?? ('simple' as const), buttons: extras.buttons }),
  ...(extras.builder === undefined ? {} : { builder: extras.builder })
});

/** The confirming turn's final action: the step after the change, with its record link. */
export const performed = (
  action: OrbitAction,
  result: { id: number; state: string }
): OrbitAction => {
  const [first] = action.steps;
  return {
    ...action,
    state: 'completed',
    steps: [
      {
        ...first,
        status: 'succeeded',
        result,
        link: { entity: first.target.entity, id: result.id, subtype: first.target.subtype }
      }
    ]
  };
};

export const ended = (
  action: OrbitAction,
  status: 'pending_approval' | 'refused' | 'failed',
  state: 'completed' | 'failed' = 'completed'
): OrbitAction => ({
  ...action,
  state,
  steps: [{ ...action.steps[0], status }]
});

export const ruleTarget = (id: number, name: string, state = 'active'): OrbitActionTarget => ({
  entity: 'rule',
  id,
  name,
  state,
  subtype: null
});
